import { useCallback, useEffect, useId, useRef, useState } from "react";
import { AppIcon } from "./AppIcon";

export type MobileEntityHeaderMenuItem = {
  id: string;
  label: string;
  onSelect: () => void;
  danger?: boolean;
  disabled?: boolean;
};

type MobileEntityHeaderProps = {
  onBack: () => void;
  title: string;
  /** Native tooltip / full string */
  titleTitle?: string;
  subtitle?: string | null;
  /** Rendered after the primary title on the same line (e.g. presence dot for DM). */
  titleSuffix?: React.ReactNode;
  showCall?: boolean;
  onCall?: () => void;
  callTitle?: string;
  overflowItems: MobileEntityHeaderMenuItem[];
  /** When false, no overflow button (empty menu). */
  showOverflow?: boolean;
  /** Открыть закреплённые сообщения (кнопка слева от «⋯»). */
  onPinnedMessages?: () => void;
  /** Число закреплённых для бейджа на кнопке. */
  pinnedCount?: number;
  /** Optional analytics / tests (chat shell compatibility). */
  dataSupportsTopics?: boolean;
  dataSupportsRoles?: boolean;
};

export function MobileEntityHeader({
  onBack,
  title,
  titleTitle,
  subtitle,
  titleSuffix,
  showCall,
  onCall,
  callTitle = "Звонок",
  overflowItems,
  showOverflow = true,
  onPinnedMessages,
  pinnedCount,
  dataSupportsTopics,
  dataSupportsRoles,
}: MobileEntityHeaderProps) {
  const menuId = useId();
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement | null>(null);

  const close = useCallback(() => setOpen(false), []);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") close();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, close]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      const el = wrapRef.current;
      if (el && !el.contains(e.target as Node)) close();
    };
    window.addEventListener("mousedown", onDown);
    return () => window.removeEventListener("mousedown", onDown);
  }, [open, close]);

  const hasOverflow = showOverflow && overflowItems.length > 0;

  return (
    <div
      className="chat-shell__head mobile-entity-header"
      data-supports-topics={dataSupportsTopics === undefined ? undefined : dataSupportsTopics ? "1" : "0"}
      data-supports-roles={dataSupportsRoles === undefined ? undefined : dataSupportsRoles ? "1" : "0"}
    >
      <button type="button" className="chat-shell__back" onClick={onBack}>
        {"<"}
      </button>
      <div className="mobile-entity-header__center">
        <div className="mobile-entity-header__title-row">
          <div className="mobile-entity-header__titles">
            <div className="mobile-entity-header__title-line">
              <span className="chat-shell__contact mobile-entity-header__title" title={titleTitle ?? title}>
                {title}
              </span>
              {titleSuffix}
            </div>
            {subtitle ? (
              <span className="chat-shell__topic-workspace mobile-entity-header__subtitle" title={subtitle}>
                {subtitle}
              </span>
            ) : null}
          </div>
        </div>
      </div>
      <div className="mobile-entity-header__actions">
        {showCall && onCall ? (
          <button
            type="button"
            className="chat-shell__head-icon-btn mobile-entity-header__call-btn"
            onClick={onCall}
            aria-label="Голосовой звонок"
            title={callTitle}
          >
            <AppIcon name="phone" className="chat-shell__head-icon-svg" />
          </button>
        ) : null}
        {onPinnedMessages ? (
          <button
            type="button"
            className="chat-shell__head-icon-btn chat-shell__head-icon-btn--with-badge"
            onClick={onPinnedMessages}
            aria-label="Закреплённые сообщения"
            title="Закреплённые"
          >
            <AppIcon name="pin" className="chat-shell__head-icon-svg" />
            {(pinnedCount ?? 0) > 0 ? (
              <span className="chat-shell__head-badge">{pinnedCount}</span>
            ) : null}
          </button>
        ) : null}
        {hasOverflow ? (
          <div className="mobile-entity-header__overflow-wrap" ref={wrapRef}>
            <button
              type="button"
              className="chat-shell__head-icon-btn mobile-entity-header__overflow-btn"
              aria-label="Меню"
              title="Меню"
              aria-haspopup="menu"
              aria-expanded={open}
              aria-controls={open ? menuId : undefined}
              onClick={() => setOpen((v) => !v)}
            >
              <AppIcon name="ellipsis-vertical" className="chat-shell__head-icon-svg" />
            </button>
            {open ? (
              <>
                <button
                  type="button"
                  className="mobile-entity-header__menu-backdrop"
                  aria-hidden
                  tabIndex={-1}
                  onClick={close}
                />
                <div id={menuId} role="menu" className="mobile-entity-header__menu">
                  {overflowItems.map((item) => (
                    <button
                      key={item.id}
                      type="button"
                      role="menuitem"
                      className={`mobile-entity-header__menu-item${item.danger ? " mobile-entity-header__menu-item--danger" : ""}`}
                      disabled={item.disabled}
                      onClick={() => {
                        if (item.disabled) return;
                        close();
                        item.onSelect();
                      }}
                    >
                      {item.label}
                    </button>
                  ))}
                </div>
              </>
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  );
}
